import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  GenerationalDimensionAverage,
  GenerationalParticipant,
  GenerationalResponse,
} from '@/api/types';
import type { AnalyticsFilterParams } from '@/api/client';
import type { GenView } from '@/types/analytics.types';
import { useAnalyticsGenerationalParticipants } from '@/hooks/useAnalytics';
import { MOCK_GEN_COHORTS } from '@/data/mockAnalytics';
import { scoreColor, fmtScore } from '@/utils/analytics';
import { DimensionFilterNotice } from '../components/DimensionFilterNotice';
import { DimensionLabel } from '../components/DimensionLabel';
import { useOuNameResolver, usePersonNameResolver } from '../hooks/useAnalyticsNames';

interface GeneracionalTabProps {
  apiGenerational?: GenerationalResponse;
  /** Mismos filtros de OU/fecha que AnalyticsView/index.tsx ya calcula para `useAnalyticsGenerational`
   *  — se reenvían a `useAnalyticsGenerationalParticipants` en la vista "ranking". */
  filters: AnalyticsFilterParams;
  /** Dimensión seleccionada en el filtro; `null` cuando el reporte cubre todas. */
  activeDimensionName?: string | null;
}

/** Bandas reales devueltas por AnalyticsService (GenerationBand, ver GenerationHelper.cs en
 *  UserAdminService) mapeadas al catálogo de UI en MOCK_GEN_COHORTS (colores/íconos/rango de años).
 *  GenAlpha y Silent no tienen cohorte de UI definida — quedan sin bandera de color en el ranking. */
const BAND_TO_COHORT_ID: Record<string, string> = {
  BabyBoomer: 'boomer',
  GenX: 'genx',
  Millennial: 'millennial',
  GenZ: 'genz',
};

function cohortForBand(band: string) {
  const cohortId = BAND_TO_COHORT_ID[band];
  return cohortId ? MOCK_GEN_COHORTS.find((cohort) => cohort.id === cohortId) ?? null : null;
}

/** Cifras agregadas de una cohorte generacional, tal como las consumen las tres vistas. */
interface CohortStats {
  avgScore: number;
  count: number;
  evaluations: number;
}

type CohortStatsMap = Map<string, CohortStats>;

/** Contenedor de los estados de carga y vacío de las tres vistas generacionales. */
const GEN_STATE_CLASS = 'au-gen-loading';
const LOADING_KEY = 'common.loading';

/** Color de la celda "Δ Brecha": rojo si la dispersión entre generaciones es alta. */
function gapColor(gap: number): string {
  if (gap >= 0.5) return 'var(--cp-danger-light)';
  if (gap >= 0.3) return '#f59e0b';
  return '#059669';
}

/** Marca la generación con la mayor y la menor brecha de la fila; vacío si todas empatan. */
function resolveGapHighlight(gap: number, maxGap: number, minGap: number): string {
  if (maxGap === minGap) return '';
  if (gap === maxGap) return ' is-best';
  if (gap === minGap) return ' is-worst';
  return '';
}

export function GeneracionalTab({
  apiGenerational,
  filters,
  activeDimensionName = null,
}: GeneracionalTabProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<GenView>('panorama');
  const [genFilter, setGenFilter] = useState<string>('all');

  const cohortStats = useMemo(() => {
    if (!apiGenerational) return null;
    const stats: CohortStatsMap = new Map();
    for (const group of apiGenerational) {
      const cohort = cohortForBand(group.generationBand);
      if (!cohort) continue;
      stats.set(cohort.id, {
        avgScore: group.overallAverage,
        count: group.participantCount,
        evaluations: group.evaluations,
      });
    }
    return stats;
  }, [apiGenerational]);

  const totalMappedCount = useMemo(
    () => [...(cohortStats?.values() ?? [])].reduce((sum, cohort) => sum + cohort.count, 0),
    [cohortStats],
  );

  const totalWithGeneration = useMemo(
    () => apiGenerational?.reduce((sum, group) => sum + group.participantCount, 0) ?? 0,
    [apiGenerational],
  );

  const views: Array<{ id: GenView; icon: string; label: string }> = [
    { id: 'panorama', icon: 'fa-th',      label: t('analytics.gen.panorama') },
    { id: 'ranking',  icon: 'fa-list-ol', label: t('analytics.gen.ranking') },
    { id: 'brechas',  icon: 'fa-table',   label: t('analytics.gen.brechas') },
  ];

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-users" aria-hidden="true" />
            {t('analytics.gen.title')}
          </div>
          <div className="au-card-sub">{t('analytics.gen.subtitle', { n: totalWithGeneration })}</div>
        </div>
      </div>

      <div className="au-gen-view-toggle" role="tablist">
        {views.map((option) => (
          <button
            type="button"
            key={option.id}
            role="tab"
            aria-selected={view === option.id}
            className={`au-gen-view-btn${view === option.id ? ' active' : ''}`}
            onClick={() => setView(option.id)}
          >
            <i className={`fas ${option.icon}`} aria-hidden="true" />
            {option.label}
          </button>
        ))}
      </div>

      {view === 'panorama' && (
        <>
          <DimensionFilterNotice dimensionName={activeDimensionName} />
          <PanoramaView cohortStats={cohortStats} totalMappedCount={totalMappedCount} />
        </>
      )}

      {view === 'ranking' && (
        <>
          {/* El ranking se arma con el promedio general de cada persona: el endpoint de
              participantes no devuelve el desglose por dimensión. */}
          <DimensionFilterNotice
            dimensionName={activeDimensionName}
            messageKey="analytics.dimensionFilter.notApplicable"
          />
          <RankingView genFilter={genFilter} setGenFilter={setGenFilter} filters={filters} />
        </>
      )}

      {view === 'brechas' && (
        <BrechasView apiGenerational={apiGenerational} cohortStats={cohortStats} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PANORAMA
// ─────────────────────────────────────────────────────────────────────────────

interface PanoramaViewProps {
  cohortStats: CohortStatsMap | null;
  totalMappedCount: number;
}

function PanoramaView({ cohortStats, totalMappedCount }: PanoramaViewProps) {
  const { t } = useTranslation();

  if (!cohortStats) {
    return <div className={GEN_STATE_CLASS}>{t(LOADING_KEY)}</div>;
  }

  return (
    <>
      <div className="au-pct-stacked au-gen-dist-bar">
        {MOCK_GEN_COHORTS.filter((cohort) => (cohortStats.get(cohort.id)?.count ?? 0) > 0)
          .map((cohort) => {
            const stats = cohortStats.get(cohort.id)!;
            const share = totalMappedCount > 0
              ? Math.round((stats.count / totalMappedCount) * 100)
              : 0;
            return (
              <div
                key={cohort.id}
                className="au-gen-seg"
                style={{ width: `${share}%`, background: cohort.color }}
                title={`${cohort.label}: ${share}%`}
              >
                {share >= 10 && <span>{share}%</span>}
              </div>
            );
          })}
      </div>

      <div className="au-pct-stacked-legend">
        {MOCK_GEN_COHORTS.map((cohort) => (
          <span key={cohort.id}>
            <i style={{ background: cohort.color }} />
            {cohort.label}
          </span>
        ))}
      </div>

      <div className="au-gen-cards">
        {MOCK_GEN_COHORTS.map((cohort) => {
          const stats = cohortStats.get(cohort.id);

          if (!stats || stats.count === 0) {
            return (
              <div key={cohort.id} className="au-gen-card au-gen-card--empty">
                <div
                  className="au-gen-card-icon"
                  style={{ background: `${cohort.color}20`, color: cohort.color }}
                >
                  <i className={`fas ${cohort.icon}`} aria-hidden="true" />
                </div>
                <div className="au-gen-card-body">
                  <div className="au-gen-card-label" style={{ color: cohort.color }}>{cohort.label}</div>
                  <div className="au-gen-card-range">{cohort.yearStart}–{cohort.yearEnd}</div>
                  <div className="au-gen-card-empty-msg">{t('analytics.gen.noRepresentation')}</div>
                </div>
              </div>
            );
          }

          const share = totalMappedCount > 0
            ? Math.round((stats.count / totalMappedCount) * 100)
            : 0;
          const barColor = stats.avgScore > 0 ? scoreColor(stats.avgScore) : 'var(--gray-500)';

          return (
            <div key={cohort.id} className="au-gen-card">
              <div
                className="au-gen-card-icon"
                style={{ background: `${cohort.color}20`, color: cohort.color }}
              >
                <i className={`fas ${cohort.icon}`} aria-hidden="true" />
              </div>
              <div className="au-gen-card-body">
                <div className="au-gen-card-label" style={{ color: cohort.color }}>{cohort.label}</div>
                <div className="au-gen-card-range">{cohort.yearStart}–{cohort.yearEnd}</div>
                <div className="au-gen-card-count">
                  {t('analytics.gen.personasCount', { count: stats.count })} · {share}%
                </div>
                <div className="au-gen-card-count">
                  {t('analytics.summary.evaluationsCount', { count: stats.evaluations })}
                </div>
              </div>
              <div className="au-gen-card-stats">
                <div className="au-gen-stat">
                  <span className="au-gen-stat-val" style={{ color: barColor }}>
                    {stats.avgScore > 0 ? fmtScore(stats.avgScore) : '—'}
                  </span>
                  <span className="au-gen-stat-lbl">{t('analytics.gen.scoreLabel')}</span>
                </div>
              </div>
              <div className="au-gen-card-bar">
                <div
                  className="au-gen-card-bar-fill"
                  style={{ width: `${Math.round((stats.avgScore / 4) * 100)}%`, background: barColor }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RANKING — datos reales de GET /v1/analytics/generational/participants, con nombre de
// persona y de OU resueltos contra UserAdminService.
// ─────────────────────────────────────────────────────────────────────────────

interface RankingViewProps {
  genFilter: string;
  setGenFilter: (value: string) => void;
  filters: AnalyticsFilterParams;
}

function RankingView({ genFilter, setGenFilter, filters }: RankingViewProps) {
  const { t } = useTranslation();
  const { data: participants, isLoading } = useAnalyticsGenerationalParticipants(filters);

  const evaluatedIds = useMemo(
    () => (participants ?? []).map((participant) => participant.evaluatedId),
    [participants],
  );
  const { resolvePersonName } = usePersonNameResolver(evaluatedIds);
  const { resolveOuName } = useOuNameResolver();

  const participantsByCohortId = useMemo(() => {
    const byCohort: Record<string, GenerationalParticipant[]> = {};
    MOCK_GEN_COHORTS.forEach((cohort) => { byCohort[cohort.id] = []; });
    (participants ?? []).forEach((participant) => {
      const cohort = cohortForBand(participant.generationBand);
      if (cohort) byCohort[cohort.id]!.push(participant);
    });
    return byCohort;
  }, [participants]);

  const filtered = useMemo(() => {
    const base = genFilter === 'all'
      ? (participants ?? [])
      : (participantsByCohortId[genFilter] ?? []);
    return [...base].sort((first, second) => second.overallAverage - first.overallAverage);
  }, [participants, genFilter, participantsByCohortId]);

  const rankColors = ['#f59e0b', '#9ca3af', '#cd7f32'];

  return (
    <>
      <div className="au-gen-filter-row">
        <button
          type="button"
          className={`au-gen-filter-btn${genFilter === 'all' ? ' active' : ''}`}
          onClick={() => setGenFilter('all')}
        >
          {t('analytics.gen.all')}{' '}
          <span className="au-gen-filter-count">{(participants ?? []).length}</span>
        </button>
        {MOCK_GEN_COHORTS.filter((cohort) => participantsByCohortId[cohort.id]!.length > 0)
          .map((cohort) => (
            <button
              type="button"
              key={cohort.id}
              className={`au-gen-filter-btn${genFilter === cohort.id ? ' active' : ''}`}
              style={
                genFilter === cohort.id
                  ? { background: cohort.color, borderColor: cohort.color, color: '#fff' }
                  : { borderColor: cohort.color, color: cohort.color }
              }
              onClick={() => setGenFilter(genFilter === cohort.id ? 'all' : cohort.id)}
            >
              {cohort.label}{' '}
              <span className="au-gen-filter-count">{participantsByCohortId[cohort.id]!.length}</span>
            </button>
          ))}
      </div>

      <div className="au-gen-rank-list">
        {isLoading && <div className={GEN_STATE_CLASS}>{t(LOADING_KEY)}</div>}
        {!isLoading && filtered.length === 0 && (
          <div className={GEN_STATE_CLASS}>{t('analytics.gen.noData')}</div>
        )}
        {!isLoading && filtered.map((participant, index) => {
          const cohort = cohortForBand(participant.generationBand);
          const rankBg = index < 3 ? rankColors[index] : 'var(--gray-200, #e5e7eb)';
          const rankText = index < 3 ? '#fff' : 'var(--text-secondary, #6b7280)';

          return (
            <div key={participant.evaluatedId} className="au-gen-rank-item">
              <div className="au-tb-rank" style={{ background: rankBg, color: rankText }}>
                {index + 1}
              </div>
              <div
                className="au-gen-badge"
                style={{
                  background: `${cohort?.color ?? '#9ca3af'}20`,
                  color: cohort?.color ?? 'var(--text-secondary, #6b7280)',
                }}
              >
                {cohort?.label ?? participant.generationBand}
              </div>
              <div className="au-tb-info">
                <strong>{resolvePersonName(participant.evaluatedId)}</strong>
                <span>{resolveOuName(participant.ouId)}</span>
              </div>
              <div className="au-gen-rank-meta">
                <span
                  className="au-gen-rank-score"
                  style={{ color: scoreColor(participant.overallAverage) }}
                >
                  {fmtScore(participant.overallAverage)}
                </span>
                <span className="au-gen-rank-om">
                  <i className="fas fa-comment-dots" aria-hidden="true" />{' '}
                  {t('analytics.gen.responseCount', { n: participant.responseCount })}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BRECHAS — diferencia de cada generación contra el promedio general de la dimensión.
// Se reporta por comportamiento donde el fundamental los tenga configurados.
// ─────────────────────────────────────────────────────────────────────────────

interface BrechasViewProps {
  apiGenerational?: GenerationalResponse;
  cohortStats: CohortStatsMap | null;
}

/** Promedio general de cada dimensión, ponderado por el número de personas de cada banda. */
function buildOverallByDimension(
  groups: GenerationalResponse,
): Map<string, { dimension: GenerationalDimensionAverage; average: number }> {
  const totals = new Map<string, { dimension: GenerationalDimensionAverage; sum: number; count: number }>();

  for (const group of groups) {
    for (const dimension of group.dimensions) {
      if (dimension.average <= 0) continue;
      const entry = totals.get(dimension.id) ?? { dimension, sum: 0, count: 0 };
      entry.sum += dimension.average * group.participantCount;
      entry.count += group.participantCount;
      totals.set(dimension.id, entry);
    }
  }

  return new Map(
    [...totals.entries()].map(([dimensionId, entry]) => [
      dimensionId,
      { dimension: entry.dimension, average: entry.count > 0 ? entry.sum / entry.count : 0 },
    ]),
  );
}

function BrechasView({ apiGenerational, cohortStats }: BrechasViewProps) {
  const { t } = useTranslation();

  const activeCohorts = MOCK_GEN_COHORTS.filter(
    (cohort) => (cohortStats?.get(cohort.id)?.count ?? 0) > 0,
  );

  const rows = useMemo(() => {
    if (!apiGenerational) return [];
    const overallByDimension = buildOverallByDimension(apiGenerational);

    return [...overallByDimension.values()].map(({ dimension, average }) => {
      const gapsByCohortId = new Map<string, number | null>();

      for (const cohort of MOCK_GEN_COHORTS) {
        const group = apiGenerational.find(
          (candidate) => cohortForBand(candidate.generationBand)?.id === cohort.id,
        );
        const cohortAverage = group?.dimensions.find((item) => item.id === dimension.id)?.average;
        // Un promedio de 0 es el centinela de "sin observaciones" del backend, no un puntaje real.
        gapsByCohortId.set(
          cohort.id,
          !cohortAverage || average <= 0 ? null : Math.round((cohortAverage - average) * 10) / 10,
        );
      }

      const observed = activeCohorts
        .map((cohort) => gapsByCohortId.get(cohort.id))
        .filter((gap): gap is number => gap !== null && gap !== undefined);
      const maxGap = observed.length > 0 ? Math.max(...observed) : 0;
      const minGap = observed.length > 0 ? Math.min(...observed) : 0;

      return {
        dimension,
        gapsByCohortId,
        spread: Math.round((maxGap - minGap) * 10) / 10,
        maxGap,
        minGap,
      };
    });
  }, [apiGenerational, activeCohorts]);

  const widestGap = rows.length > 0
    ? rows.reduce((widest, row) => (row.spread > widest.spread ? row : widest))
    : null;

  if (!apiGenerational) {
    return <div className={GEN_STATE_CLASS}>{t(LOADING_KEY)}</div>;
  }

  if (rows.length === 0) {
    return <div className={GEN_STATE_CLASS}>{t('analytics.noData')}</div>;
  }

  return (
    <>
      {widestGap && (
        <div className="au-gen-brecha-note">
          <i className="fas fa-info-circle" aria-hidden="true" />
          {t('analytics.gen.brechaNote', {
            fund: widestGap.dimension.name,
            delta: widestGap.spread,
          })}
        </div>
      )}

      <div className="au-gen-brecha-wrap">
        <table className="au-gen-brecha-table">
          <thead>
            <tr>
              <th scope="col">{t('analytics.summary.behaviorColumn')}</th>
              {activeCohorts.map((cohort) => (
                <th key={cohort.id} scope="col">
                  <span style={{ color: cohort.color }}>{cohort.label}</span>
                </th>
              ))}
              <th scope="col">{t('analytics.gen.gapCol')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ dimension, gapsByCohortId, spread, maxGap, minGap }) => (
              <tr key={dimension.id}>
                <td data-label={t('analytics.summary.behaviorColumn')}>
                  <DimensionLabel
                    name={dimension.name}
                    isBehavior={dimension.isBehavior}
                    fundamentalName={dimension.fundamentalName}
                    hasDot={false}
                  />
                </td>
                {activeCohorts.map((cohort) => {
                  const gap = gapsByCohortId.get(cohort.id);
                  if (gap === null || gap === undefined) {
                    return (
                      <td key={cohort.id} className="au-gen-brecha-empty" data-label={cohort.label}>
                        —
                      </td>
                    );
                  }
                  const highlight = resolveGapHighlight(gap, maxGap, minGap);
                  return (
                    <td
                      key={cohort.id}
                      className={`au-gen-brecha-cell${highlight}`}
                      data-label={cohort.label}
                    >
                      <span style={{ color: gap >= 0 ? '#059669' : 'var(--cp-danger-light)' }}>
                        {gap > 0 ? '+' : ''}{fmtScore(gap)}
                      </span>
                    </td>
                  );
                })}
                <td className="au-gen-brecha-gap" data-label={t('analytics.gen.gapCol')}>
                  <span style={{ color: gapColor(spread) }}>{fmtScore(spread)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="au-gen-brecha-source">{t('analytics.gen.brechaSource')}</div>
    </>
  );
}
